/**
 * Sliding a row aside to get rid of it.
 *
 * The rules live here rather than in the component so they can be tested without a browser:
 * a gesture is the one piece of UI that cannot be checked by rendering it, and getting the
 * thresholds wrong is exactly the sort of thing that only shows up on a real phone.
 *
 * Two numbers do all the work. One decides whether the finger is sliding a row or scrolling
 * the list, and getting that wrong makes the whole screen feel broken. The other decides how
 * far is far enough to mean it.
 *
 * Leftward, because that is the direction every list on both phone platforms already removes
 * in, and a gesture that runs against the hand's habit gets tried once and abandoned.
 */

/** How far a row must travel before letting go removes it. */
export const REMOVAL_THRESHOLD_PX = 96;

/**
 * How far the finger must travel before the gesture commits to being horizontal.
 *
 * Below this it is still ambiguous, and treating it as a slide would fight the list's own
 * scrolling on every flick.
 */
const ACTIVATION_PX = 12;

/** How far past the threshold the row will keep moving, so it never slides off entirely. */
const FURTHEST_PX = 160;

export interface Travel {
  dx: number;
  dy: number;
}

/**
 * Whether this is a row being slid rather than the list being scrolled.
 *
 * Deliberately biased towards scrolling: reading the list is the common act and removing is
 * the rare one, so an ambiguous drag should scroll rather than threaten to delete something.
 */
export function isSlide({ dx, dy }: Travel): boolean {
  return dx <= -ACTIVATION_PX && -dx > Math.abs(dy);
}

/**
 * How far the row actually moves, which is not how far the finger did.
 *
 * Leftward only, and it slows as it goes: resistance past the threshold is what tells the
 * hand it has gone far enough without needing to look.
 */
export function slideTo(dx: number): number {
  if (dx >= 0) {
    return 0;
  }

  const travelled = -dx;
  if (travelled <= REMOVAL_THRESHOLD_PX) {
    return dx;
  }

  const past = travelled - REMOVAL_THRESHOLD_PX;

  return -Math.min(REMOVAL_THRESHOLD_PX + past / 2, FURTHEST_PX);
}

/** Whether letting go at this point means it. */
export function removesOnRelease(dx: number): boolean {
  return dx <= -REMOVAL_THRESHOLD_PX;
}
